<template>
  <div v-if="!isLoading && !isProductsLoading">
    <div v-if="isError" class="dashboard-content">
      <div class="alert alert-danger mb-3 mt-2 py-4" role="alert">
        <p class="mb-0">{{ $t('feeds.feed_configuration_error') }}</p>
      </div>
    </div>
    <div v-else>
      <div class="dashboard-content">
        <div class="d-flex align-items-center justify-content-between mb-4">
          <div>
            <p>{{ $t('feeds.mapping_description') }}</p>
          </div>
          <div>
            <button class="btn btn-large btn-orange" @click="setDefaultValues">
              <sliders-icon></sliders-icon>
              <span>{{ $t('feeds.field_mapping_set_default_values') }}</span>
            </button>
          </div>
        </div>
        <div class="field-mapping-container">
          <table class="table table-striped table-bordered">
            <thead class="thead-light">
              <th>{{$t('details.field')}}</th>
              <th>{{$t('global.value')}}</th>
              <th>Status</th>
            </thead>
            <transition-group name="fade" mode="out-in" tag="tbody">
              <tr v-for="(mappingRule, index) in mappingRules" v-bind:key="'mr'+index">
                <td class="field-mapping-column1">
                  <div class="feed-field">
                    <p class="mb-0 break-text">{{ mappingRule.to }}</p>
                    <div class="d-flex align-items-center">
                      <h6 class="mb-0" v-if="mappingRule.status === 'required'"><b-badge variant="danger">{{ $t('validations.required') }}</b-badge></h6>
                      <h6 class="mb-0" v-else-if="mappingRule.status === 'recommended'"><b-badge variant="success">{{ $t('validations.recommended') }}</b-badge></h6>
                      <h6 class="mb-0" v-else><b-badge variant="info">{{ $t('validations.optional') }}</b-badge></h6>
                      <img class="tooltip-icon icon-small mx-4 mt-1" :id="'tooltip-feed-field'+index" v-bind:src="'/app/images/icons/info.svg'" />
                      <b-popover :target="'tooltip-feed-field'+index" variant="warning" triggers="hover" placement="bottomleft">
                        <p>{{ mappingRule.tooltip.PL }}</p>
                      </b-popover>
                    </div>
                  </div>
                </td>
                <td class="field-mapping-column2 d-flex justify-content-around align-items-center">
                  <multiselect
                    @select="onSourceVariableSelect($event, mappingRule)"
                    label="labelBracket"
                    track-by="labelBracket"
                    v-model="mappingRule.selectedSourceVariable"
                    v-show="!mappingRule.custom"
                    :allow-empty="false"
                    :deselect-label="''"
                    :id="'sourceVariableMultiselect'+index"
                    :options="mappingRule.sourceVariables"
                    :placeholder="$t('global.select_or_type')"
                    :searchable="true"
                    :select-label="''"
                    :selected-label="''"
                    :value="mappingRule.from">
                    <template slot="singleLabel" slot-scope="props">
                      <span class="option__title">{{ props.option.field }} <span class="text-gray-text">[{{ props.option.source }}]</span></span>
                    </template>
                    <template slot="option" slot-scope="props">
                      <span class="option__title">{{ props.option.field }} <span class="source-name">[{{ props.option.source }}]</span></span>
                    </template>
                    <span slot="noResult">{{ $t('global.multiselect_search_not_found') }}</span>
                  </multiselect>
                  <div v-show="mappingRule.custom" :class="{'d-flex': mappingRule.custom, 'align-items-center': true, 'custom-source-variable-label': true }">
                    <p class="ml-2 mb-1 pl-3">{{ mappingRule.from }}</p>
                  </div>
                  <b-form-checkbox class="mr-4" @change="setCustomVariable(mappingRule)" :id="'customVariable'+index" v-model="mappingRule.custom"><span>{{$t('global.own')}}</span><span>{{$t('global.variable')}}</span></b-form-checkbox>
                </td>
                <td class="field-mapping-column3 px-3">
                  <div class="d-flex justify-content-center">
                    <div v-if="mappingRule.from !== ''" class="mapping-rule-status d-flex align-items-center justify-content-center">
                      <img class="icon-small" :id="'mapping-rule-validation-success'+index" v-bind:src="'/app/images/icons/success.svg'" />
                      <b-popover :target="'mapping-rule-validation-success'+index" variant="success" triggers="hover" placement="bottomleft">
                        <p class="mb-0 ml-3">{{ $t('global.everything_is_ok') }}</p>
                      </b-popover>
                    </div>
                    <div v-else class="mapping-rule-status d-flex align-items-center justify-content-center">
                      <img class="icon-small" :id="'mapping-rule-validation-error'+index" v-bind:src="'/app/images/icons/error.svg'" />
                      <b-popover :target="'mapping-rule-validation-error'+index" variant="danger" triggers="hover" placement="bottomleft">
                        <p class="mb-0 ml-3">{{ $t('global.set_value') }}</p>
                      </b-popover>
                    </div>
                    <div v-if="mappingRule.status !== 'required'" class="mapping-rule-remove" @click="deleteAttribute(index, mappingRule.to)">
                      <trash-2-icon></trash-2-icon>
                    </div>
                  </div>
                </td>
              </tr>
            </transition-group>
          </table>
        </div>
      </div>
      <div class="d-flex justify-content-between field-mapping-footer">
        <router-link :to="{ name: 'feed.create.main-settings', params: { id: $route.params.projectId, feed: feed }}" v-bind:class="{ 'disabled-router-link': $route.params.feedId !== undefined }">
          <button class="btn btn-small btn-gray-dark" :disabled="$route.params.feedId !== undefined">
            <arrow-left-circle-icon></arrow-left-circle-icon>
            <span>{{ $t('global.back') }}</span>
          </button>
        </router-link>
        <div class="d-flex">
          <multiselect
            class="mr-4"
            label="name"
            track-by="name"
            v-model="selectedAttribute"
            :deselect-label="''"
            :multiple="false"
            :options="additionalFields"
            :placeholder="$t('global.select_or_type')"
            :searchable="true"
            :select-label="''"
            :selected-label="''">
            <template slot="singleLabel" slot-scope="props">
              <span class="option__title d-flex align-items-center justify-content-between w-100">
                <span>{{ props.option.name }}</span>
                <span class="mr-2">
                  <h6 class="mb-0" v-if="props.option.status === 'required'"><b-badge variant="danger">{{  $t('global.required') }}</b-badge></h6>
                  <h6 class="mb-0" v-else-if="props.option.status === 'recommended'"><b-badge variant="success">{{  $t('global.recommended') }}</b-badge></h6>
                  <h6 class="mb-0" v-else><b-badge variant="info">{{  $t('global.optional') }}</b-badge></h6>
                </span>
              </span>
            </template>
            <template slot="option" slot-scope="props">
              <span class="option__title d-flex align-items-center justify-content-between w-100">
                <span>{{ props.option.name }}</span>
                <span class="mr-2">
                  <h6 class="mb-0" v-if="props.option.status === 'required'"><b-badge variant="danger">{{  $t('global.required') }}</b-badge></h6>
                  <h6 class="mb-0" v-else-if="props.option.status === 'recommended'"><b-badge variant="success">{{  $t('global.recommended') }}</b-badge></h6>
                  <h6 class="mb-0" v-else><b-badge variant="info">{{  $t('global.optional') }}</b-badge></h6>
                </span>
              </span>
            </template>
            <span slot="noResult">{{ $t('global.multiselect_search_not_found') }}</span>
          </multiselect>
          <button class="btn btn-medium btn-orange" @click="addAttribute">
            <plus-circle-icon></plus-circle-icon>
            <span>{{ $t('feeds.add_attribute') }}</span>
          </button>
        </div>
        <router-link :to="{ name: 'feed.edit.rules', params: { id: $route.params.projectId, feedId: feed.id, feed: feed }}" v-if="$route.params.feedId !== undefined" v-bind:class="{ 'disabled-router-link': !isValid }">
          <button class="btn btn-small btn-orange-dark" :disabled="!isValid">
            <arrow-right-circle-icon></arrow-right-circle-icon>
            <span>{{$t('global.next')}}</span>
          </button>
        </router-link>
        <router-link :to="{ name: 'feed.create.rules', params: { id: $route.params.projectId, feed: feed }}" v-else v-bind:class="{ 'disabled-router-link': !isValid }">
          <button class="btn btn-small btn-orange-dark" :disabled="!isValid">
            <arrow-right-circle-icon></arrow-right-circle-icon>
            <span>{{$t('global.next')}}</span>
          </button>
        </router-link>
      </div>
      <div class="feed-preview-response">
        <div v-if="!isFeedPreviewLoading">
          <h1 class="my-5"><strong>Podgląd feeda</strong></h1>
          <table v-if="feedPreviewResponse !== null" class="table table-striped table-bordered table-feed-final">
            <div>
              <thead class="thead-light">
                <th v-for="(value, key) in feed.config.mappingRules" v-bind:key="key">
                  <div class="d-flex justify-content-center align-items-center">
                    <div>{{ value.to }}</div>
                  </div>
                </th>
              </thead>
              <tbody>
                <tr v-for="(product, index) in feedPreviewResponse" v-bind:key="index">
                  <td v-for="(value, column) in feed.config.mappingRules" v-bind:key="column">
                    <p class="break-text">{{ product[value.to] }}</p>
                  </td>
                </tr>
              </tbody>
            </div>
          </table>
        </div>
        <div v-else>
          <div class="spinner-border text-orange" style="position: relative; top: 2rem;" role="status"></div>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="spinner-border text-orange" role="status"></div>
  </div>
</template>

<script>
import products from '@/mixins/products'
import feeds from '@/mixins/feeds'
import previewFeed from '@/mixins/previewFeed'
import axios from 'axios'
import { ArrowLeftCircleIcon, ArrowRightCircleIcon, PlusCircleIcon, SlidersIcon, Trash2Icon } from 'vue-feather-icons'
import Multiselect from 'vue-multiselect'

export default {
  name: 'FieldMapping',

  data () {
    return {
      feedPreviewResponse: null,
      isFeedPreviewLoading: false,
      mappingRules: this.feed.config.mappingRules,
      selectedAttribute: null
    }
  },

  props: {
    feed: { required: true, type: Object }
  },

  mixins: [feeds, products, previewFeed],

  created () {
    this.initSourceVariables(this.feed.feedType)
  },

  methods: {
    update () {
      axios.get(process.env.VUE_APP_API_ENDPOINT + '/feed/update/' + this.$route.params.id)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            window.dataLayer.push({ event: 'runFeedsUpdate' })
            this.$bvToast.toast(this.$t('feeds.feeds_updating_description'), {
              autoHideDelay: 3000,
              solid: true,
              title: this.$t('feeds.feeds_updating'),
              variant: 'info'
            })
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$bvToast.toast(this.$t('feeds.feeds_updating_error_description2'), {
              autoHideDelay: 3000,
              solid: true,
              title: this.$t('feeds.feeds_updating_error'),
              variant: 'danger'
            })
          }
        })
    },

    onSourceVariableSelect (sourceVariable, mappingRule) {
      mappingRule.from = sourceVariable.field + ' — ' + sourceVariable.source
      mappingRule.fromLabel = sourceVariable.labelBracket
    },

    setCustomVariable (mappingRule) {
      if (mappingRule.custom) {
        mappingRule.from = mappingRule.to
        mappingRule.fromLabel = mappingRule.to
        if ((mappingRule.selectedSourceVariable !== undefined) && (mappingRule.selectedSourceVariable !== '')) mappingRule.previousSelectedSourceVariable = mappingRule.selectedSourceVariable
        mappingRule.selectedSourceVariable = ''
      } else {
        if (mappingRule.previousSelectedSourceVariable !== undefined) {
          mappingRule.selectedSourceVariable = mappingRule.previousSelectedSourceVariable
          mappingRule.from = mappingRule.selectedSourceVariable.field + ' — ' + mappingRule.selectedSourceVariable.source
          mappingRule.fromLabel = mappingRule.selectedSourceVariable.labelBracket
        } else {
          mappingRule.from = ''
        }
      }
    },

    addAttribute () {
      if (this.selectedAttribute !== null) {
        const sortedSourceVariables = this.calculateSimilarity(this.selectedAttribute.name).sort((a, b) => b.similarity - a.similarity)
        this.mappingRules.push({ custom: false, from: '', sourceVariables: sortedSourceVariables, status: this.selectedAttribute.status, to: this.selectedAttribute.name, tooltip: this.selectedAttribute.tooltip })
        this.additionalFields.splice(this.additionalFields.findIndex(field => field.name === this.selectedAttribute.name), 1)
        this.selectedAttribute = null
      }
    },

    deleteAttribute (index, fieldName) {
      this.mappingRules.splice(index, 1)
      const fieldIndex = this.availableFields.findIndex(field => field.name === fieldName)
      this.additionalFields.push(this.availableFields[fieldIndex])
    },

    setDefaultValues () {
      this.mappingRules.forEach((mappingRule) => {
        mappingRule.custom = false
        mappingRule.selectedSourceVariable = mappingRule.sourceVariables[0]
        mappingRule.from = mappingRule.selectedSourceVariable.field + ' — ' + mappingRule.selectedSourceVariable.source
      })
    }
  },

  computed: {
    isValid () {
      if (this.mappingRules.some(rule => rule.from === '')) return false
      else return true
    },

    feedConfig () {
      return {
        name: this.feed.name,
        active: this.feed.active,
        config: {
          resultType: this.feedResultType,
          formatRules: this.feed.config.formatRules.map(function (formatRule) {
            return {
              conditions: formatRule.conditions.map(function (condition) {
                return {
                  columnName: condition.columnName,
                  conditionType: condition.conditionType,
                  conditionString: condition.conditionString
                }
              }),
              columnName: formatRule.columnName,
              operationType: formatRule.operationType,
              toReplace: formatRule.toReplace,
              replaceWith: formatRule.replaceWith
            }
          }),
          mappingRules: this.feed.config.mappingRules.map(function (mappingRule) {
            return { from: mappingRule.from, to: mappingRule.to }
          }),
          filterRules: this.feed.config.filterRules.map(function (filterRule) {
            return {
              conditions: filterRule.conditions.map(function (condition) {
                let columnName = ''
                if (condition.filteringField !== null && condition.filteringField !== undefined) columnName = condition.filteringField.value
                let conditionType = ''
                if (condition.filteringType !== null && condition.filteringType !== undefined) conditionType = condition.filteringType.value
                return {
                  columnName: columnName,
                  conditionType: conditionType,
                  conditionString: condition.conditionString
                }
              })
            }
          }),
          taggingRules: this.feed.config.taggingRules.map(function (taggingRule) {
            return { tag: taggingRule.tag, value: taggingRule.value, isDynamic: taggingRule.isDynamic }
          })
        },
        feedType: this.feed.feedType
      }
    }
  },

  watch: {
    mappingRules: {
      handler (newVal) {
        if (this.isValid) this.previewFeed(this.$route.params.projectId, this.feedConfig)
      },
      deep: true
    }
  },

  components: { ArrowLeftCircleIcon, ArrowRightCircleIcon, Multiselect, PlusCircleIcon, SlidersIcon, Trash2Icon }
}
</script>
